import React from 'react';
import config from '../../config';
import { decodeJwtPayload, getCognitoGroupsFromPayload } from '../../helpers/auth';

const PKCE_VERIFIER_KEY = 'launchpad_pkce_verifier';
const OAUTH_STATE_KEY = 'launchpad_oauth_state';
const AUTH_ERROR_KEY = 'launchpad_auth_error';
const ignoreError = () => undefined;

const readStorageValue = (key) => {
  const fromSession = sessionStorage.getItem(key);
  if (fromSession) {
    return fromSession;
  }
  try {
    return localStorage.getItem(key) || '';
  } catch {
    return '';
  }
};

const clearAuthFlowStorage = () => {
  sessionStorage.removeItem(PKCE_VERIFIER_KEY);
  sessionStorage.removeItem(OAUTH_STATE_KEY);
  try {
    localStorage.removeItem(PKCE_VERIFIER_KEY);
    localStorage.removeItem(OAUTH_STATE_KEY);
  } catch {
    ignoreError();
  }
};

const setRejected = ({ reason, details } = {}) => {
  sessionStorage.setItem('isAuthenticated', 'rejected');
  sessionStorage.removeItem('userRoles');
  sessionStorage.removeItem('token');
  sessionStorage.removeItem('idToken');
  sessionStorage.removeItem('accessToken');
  sessionStorage.removeItem('refreshToken');
  if (reason) {
    sessionStorage.setItem(AUTH_ERROR_KEY, JSON.stringify({
      reason,
      details: details || '',
      ts: new Date().toISOString(),
    }));
  } else {
    sessionStorage.removeItem(AUTH_ERROR_KEY);
  }
  clearAuthFlowStorage();
};

const setAuthenticated = ({
  token,
  idToken,
  accessToken,
  refreshToken,
}) => {
  if (token) {
    sessionStorage.setItem('token', token);
  }
  if (idToken) {
    sessionStorage.setItem('idToken', idToken);
  }
  if (accessToken) {
    sessionStorage.setItem('accessToken', accessToken);
  }
  if (refreshToken) {
    sessionStorage.setItem('refreshToken', refreshToken);
  }
  const payload = decodeJwtPayload(idToken || accessToken || token);
  const roles = getCognitoGroupsFromPayload(payload);
  sessionStorage.setItem('userRoles', JSON.stringify(roles));
  sessionStorage.setItem('isAuthenticated', 'authenticated');
};

const normalizeLaunchpadUrl = (value) => String(value || '')
  .trim()
  .replace(/^['"]+|['"]+$/g, '')
  .replace(/\s+/g, '');

const parseRelayStateParams = (authorizeUrl) => {
  const relayState = (authorizeUrl.searchParams.get('RelayState') || '').trim();
  if (!relayState) {
    return null;
  }
  try {
    return new URLSearchParams(decodeURIComponent(relayState));
  } catch {
    return new URLSearchParams(relayState);
  }
};

const getAuthorizeParam = (authorizeUrl, key) => {
  const direct = (authorizeUrl.searchParams.get(key) || '').trim();
  if (direct) {
    return direct;
  }
  const relayParams = parseRelayStateParams(authorizeUrl);
  if (!relayParams) {
    return '';
  }
  return (relayParams.get(key) || '').trim();
};

const getTokenEndpoint = (authorizeUrl) => {
  const explicitTokenUrl = normalizeLaunchpadUrl(config.launchpadTokenURL);
  if (explicitTokenUrl) {
    return explicitTokenUrl;
  }
  const tokenUrlParam = normalizeLaunchpadUrl(getAuthorizeParam(authorizeUrl, 'token_url'));
  if (tokenUrlParam) {
    return tokenUrlParam;
  }
  if (/\/oauth2\/authorize\/?$/i.test(authorizeUrl.pathname)) {
    return `${authorizeUrl.origin}/oauth2/token`;
  }
  return '';
};

const exchangeCodeForTokens = async ({ code, verifier }) => {
  const launchpadURL = normalizeLaunchpadUrl(config.launchpadURL);
  const redirectUri = `${window.location.origin}/auth`;
  if (!launchpadURL || !verifier) {
    return { ok: false, reason: 'missing_launchpad_url_or_verifier' };
  }
  const authorizeUrl = new URL(launchpadURL);
  const clientId = getAuthorizeParam(authorizeUrl, 'client_id');
  const tokenEndpoint = getTokenEndpoint(authorizeUrl);
  if (!clientId || !tokenEndpoint) {
    return { ok: false, reason: 'missing_client_id_or_token_endpoint' };
  }
  try {
    const response = await fetch(tokenEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        grant_type: 'authorization_code',
        client_id: clientId,
        code,
        redirect_uri: redirectUri,
        code_verifier: verifier,
      }).toString(),
    });
    const raw = await response.text();
    let parsed = null;
    try {
      parsed = JSON.parse(raw);
    } catch {
      parsed = null;
    }
    if (!response.ok) {
      return {
        ok: false,
        reason: 'token_exchange_http_error',
        details: JSON.stringify({
          status: response.status,
          statusText: response.statusText,
          body: parsed || raw,
        }),
      };
    }
    return { ok: true, tokens: parsed || {} };
  } catch (error) {
    return {
      ok: false,
      reason: 'token_exchange_network_error',
      details: error instanceof Error ? error.message : String(error),
    };
  }
};

function ProcessToken() {
  React.useEffect(() => {
    const run = async () => {
      try {
        const url = new URL(window.location.href);
        const error = url.searchParams.get('error');
        const code = url.searchParams.get('code');
        const hashParams = new URLSearchParams((url.hash || '').replace(/^#/, ''));
        const hashAccessToken = hashParams.get('access_token');
        const hashIdToken = hashParams.get('id_token');
        const state = url.searchParams.get('state') || hashParams.get('state');
        const verifier = readStorageValue(PKCE_VERIFIER_KEY);
        if (error) {
          setRejected({ reason: 'oauth_error', details: error });
          window.location.replace('/login');
          return;
        }
        const expectedState = readStorageValue(OAUTH_STATE_KEY);
        if (!expectedState) {
          setRejected({
            reason: 'missing_expected_state',
          });
          window.location.replace('/login');
          return;
        }
        if (state && state !== expectedState) {
          setRejected({
            reason: 'state_mismatch',
            details: JSON.stringify({
              hasExpectedState: Boolean(expectedState),
              returnedState: state || '',
            }),
          });
          window.location.replace('/login');
          return;
        }
        if (!state && !hashAccessToken && !hashIdToken && !verifier) {
          setRejected({
            reason: 'missing_state_and_verifier',
          });
          window.location.replace('/login');
          return;
        }
        if (hashAccessToken || hashIdToken) {
          setAuthenticated({
            token: hashAccessToken || hashIdToken,
            idToken: hashIdToken,
            accessToken: hashAccessToken,
          });
          clearAuthFlowStorage();
          window.location.replace('/#');
          return;
        }
        if (!code) {
          setRejected({ reason: 'missing_code' });
          window.location.replace('/login');
          return;
        }
        const tokenResult = await exchangeCodeForTokens({ code, verifier });
        if (!tokenResult.ok) {
          setRejected({ reason: tokenResult.reason, details: tokenResult.details });
          window.location.replace('/login');
          return;
        }
        const tokenResponse = tokenResult.tokens;
        if (!tokenResponse || (!tokenResponse.access_token && !tokenResponse.id_token)) {
          setRejected({ reason: 'token_exchange_missing_tokens' });
          window.location.replace('/login');
          return;
        }
        setAuthenticated({
          token: tokenResponse.access_token || tokenResponse.id_token,
          idToken: tokenResponse.id_token,
          accessToken: tokenResponse.access_token,
          refreshToken: tokenResponse.refresh_token,
        });
        clearAuthFlowStorage();
        window.location.replace('/#');
      } catch {
        setRejected({ reason: 'unexpected_exception' });
        window.location.replace('/login');
      }
    };
    run();
  }, []);

  if (sessionStorage.getItem('isAuthenticated') === 'rejected') {
    return <div>User is not authenticated with launchpad</div>;
  }
  return <div>Signing in...</div>;
}

export default ProcessToken;
